import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DECK_SLIDES } from './deckData';
import { Slide01Vision } from './slides/Slide01Vision';
import { Slide02Problem } from './slides/Slide02Problem';
import { Slide03Solution } from './slides/Slide03Solution';
import { Slide04DeepTechMoat } from './slides/Slide04DeepTechMoat';
import { Slide05MarketOpportunity } from './slides/Slide05MarketOpportunity';
import { Slide06BusinessModel } from './slides/Slide06BusinessModel';
import { Slide07CompetitiveMatrix } from './slides/Slide07CompetitiveMatrix';
import { Slide08TractionGTM } from './slides/Slide08TractionGTM';
import { Slide09Roadmap } from './slides/Slide09Roadmap';
import { Slide10Team } from './slides/Slide10Team';
import { Slide11TheAsk } from './slides/Slide11TheAsk';
import { Slide12Contact } from './slides/Slide12Contact';
import {
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Minimize2,
  MessageSquareQuote,
  X,
  Printer,
  ShieldCheck,
  Sun,
  Moon,
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

export const PitchDeck: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const [currentSlideIndex, setCurrentSlideIndex] = useState<number>(0);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showSpeakerNotes, setShowSpeakerNotes] = useState<boolean>(false);
  const deckContainerRef = useRef<HTMLDivElement>(null);

  const touchStartXRef = useRef<number | null>(null);
  const touchStartYRef = useRef<number | null>(null);

  const totalSlides = DECK_SLIDES.length;
  const currentSlide = DECK_SLIDES[currentSlideIndex];

  // Navigation handlers
  const goToNext = useCallback(() => {
    setCurrentSlideIndex((prev) => Math.min(prev + 1, totalSlides - 1));
  }, [totalSlides]);

  const goToPrev = useCallback(() => {
    setCurrentSlideIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  const goToSlide = (index: number) => {
    if (index >= 0 && index < totalSlides) {
      setCurrentSlideIndex(index);
    }
  };

  // Touch Swipe Handlers for Mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0].clientX;
    touchStartYRef.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartXRef.current === null || touchStartYRef.current === null) return;
    const deltaX = e.changedTouches[0].clientX - touchStartXRef.current;
    const deltaY = e.changedTouches[0].clientY - touchStartYRef.current;

    // Trigger swipe if horizontal movement is > 45px and dominant
    if (Math.abs(deltaX) > 45 && Math.abs(deltaX) > Math.abs(deltaY) * 1.3) {
      if (deltaX < 0) {
        goToNext();
      } else {
        goToPrev();
      }
    }
    touchStartXRef.current = null;
    touchStartYRef.current = null;
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      deckContainerRef.current?.requestFullscreen().catch(() => {});
      setIsFullscreen(true);
    } else {
      document.exitFullscreen().catch(() => {});
      setIsFullscreen(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (['input', 'textarea', 'select'].includes((e.target as HTMLElement).tagName.toLowerCase())) {
        return;
      }

      switch (e.key) {
        case 'ArrowRight':
        case 'PageDown':
        case ' ':
          e.preventDefault();
          goToNext();
          break;
        case 'ArrowLeft':
        case 'PageUp':
          e.preventDefault();
          goToPrev();
          break;
        case 'Home':
          e.preventDefault();
          setCurrentSlideIndex(0);
          break;
        case 'End':
          e.preventDefault();
          setCurrentSlideIndex(totalSlides - 1);
          break;
        case 'f':
        case 'F':
          e.preventDefault();
          toggleFullscreen();
          break;
        case 'n':
        case 'N':
          e.preventDefault();
          setShowSpeakerNotes((prev) => !prev);
          break;
        case 'Escape':
          setShowSpeakerNotes(false);
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToNext, goToPrev, totalSlides]);

  // Fullscreen change listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Render active slide
  const renderSlideContent = (index: number) => {
    switch (index) {
      case 0:
        return <Slide01Vision onNext={goToNext} />;
      case 1:
        return <Slide02Problem />;
      case 2:
        return <Slide03Solution />;
      case 3:
        return <Slide04DeepTechMoat />;
      case 4:
        return <Slide05MarketOpportunity />;
      case 5:
        return <Slide06BusinessModel />;
      case 6:
        return <Slide07CompetitiveMatrix />;
      case 7:
        return <Slide08TractionGTM />;
      case 8:
        return <Slide09Roadmap />;
      case 9:
        return <Slide10Team />;
      case 10:
        return <Slide11TheAsk />;
      case 11:
        return <Slide12Contact onRestart={() => setCurrentSlideIndex(0)} onPrint={handlePrint} />;
      default:
        return <Slide01Vision onNext={goToNext} />;
    }
  };

  return (
    <div
      ref={deckContainerRef}
      className="deck-root min-h-screen bg-slate-100 dark:bg-navy-950 text-slate-900 dark:text-slate-100 flex flex-col justify-between select-none relative overflow-x-hidden font-sans"
    >
      {/* Top Deck Header Bar (Hidden in Print) */}
      <header className="deck-control-bar relative z-30 h-14 px-3 sm:px-6 flex items-center justify-between border-b border-slate-200 dark:border-navy-800 bg-white/95 dark:bg-navy-900/95 backdrop-blur-md">
        {/* Brand & Category */}
        <div className="flex items-center gap-2.5 sm:gap-3">
          <div className="w-8 h-8 rounded-lg bg-gold-500 flex items-center justify-center shadow-gold-glow shrink-0">
            <ShieldCheck className="w-5 h-5 text-navy-950 stroke-[2.5]" />
          </div>
          <div className="hidden sm:block">
            <span className="font-mono text-xs font-bold text-slate-900 dark:text-white tracking-wider">
              WORKOUSE<span className="text-gold-500">.AI</span>
            </span>
            <span className="text-[11px] font-mono text-slate-500 dark:text-slate-400 block -mt-1">
              Investor Presentation
            </span>
          </div>
        </div>

        {/* Slide Selector & Title */}
        <div className="flex items-center gap-2 flex-1 max-w-xs sm:max-w-md mx-2 sm:mx-4">
          <select
            aria-label="Select slide"
            value={currentSlideIndex}
            onChange={(e) => goToSlide(Number(e.target.value))}
            className="w-full px-2.5 py-1 rounded-lg text-xs font-mono font-medium bg-slate-100 dark:bg-navy-800 border border-slate-300 dark:border-navy-700 text-slate-900 dark:text-slate-200 focus:outline-none focus:border-gold-500 cursor-pointer truncate"
          >
            {DECK_SLIDES.map((s, idx) => (
              <option key={s.id} value={idx}>
                {String(idx + 1).padStart(2, '0')}. {s.title}
              </option>
            ))}
          </select>
        </div>

        {/* Top Right Controls */}
        <div className="flex items-center gap-1 sm:gap-2">
          {/* Speaker Notes Toggle */}
          <button
            onClick={() => setShowSpeakerNotes((prev) => !prev)}
            title="Speaker Notes (Shortcut: N)"
            className={`p-2 rounded-lg text-xs font-mono flex items-center gap-1.5 transition-all border ${
              showSpeakerNotes
                ? 'bg-gold-500 text-navy-950 border-gold-500 font-bold shadow-gold-glow'
                : 'bg-slate-100 dark:bg-navy-800 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-navy-700 hover:border-gold-500'
            }`}
          >
            <MessageSquareQuote className="w-4 h-4" />
            <span className="hidden md:inline">Notes</span>
          </button>

          {/* Export / Print PDF */}
          <button
            onClick={handlePrint}
            title="Export / Print Deck to PDF"
            className="p-2 rounded-lg text-xs font-mono bg-slate-100 dark:bg-navy-800 hover:bg-slate-200 dark:hover:bg-navy-700 text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-navy-700 transition-all flex items-center gap-1.5"
          >
            <Printer className="w-4 h-4" />
            <span className="hidden md:inline">PDF</span>
          </button>

          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            aria-label="Toggle theme"
            className="p-2 rounded-lg bg-slate-100 dark:bg-navy-800 hover:bg-slate-200 dark:hover:bg-navy-700 text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-navy-700 transition-all"
          >
            {theme === 'dark' ? <Sun className="w-4 h-4 text-gold-400" /> : <Moon className="w-4 h-4 text-slate-700" />}
          </button>

          {/* Fullscreen Toggle */}
          <button
            onClick={toggleFullscreen}
            aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            title="Toggle Fullscreen (Shortcut: F)"
            className="p-2 rounded-lg bg-slate-100 dark:bg-navy-800 hover:bg-slate-200 dark:hover:bg-navy-700 text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-navy-700 transition-all hidden xs:flex"
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
        </div>
      </header>

      {/* Main Slide Presentation Stage */}
      <main
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        className="deck-main-viewport flex-1 flex items-center justify-center p-2 sm:p-4 md:p-6 lg:p-8 min-h-0 relative touch-pan-y"
      >
        {/* Slide Frame (Fluid on mobile, 16:9 on sm+) */}
        <div className="deck-slide-frame w-full max-w-6xl h-full min-h-[480px] sm:min-h-0 sm:aspect-[16/9] max-h-[86vh] sm:max-h-[82vh] bg-white dark:bg-navy-900 rounded-2xl border border-slate-200 dark:border-navy-800 shadow-xl overflow-y-auto sm:overflow-hidden flex flex-col justify-between relative transition-all">
          {renderSlideContent(currentSlideIndex)}
        </div>

        {/* Mobile Backdrop for Notes */}
        {showSpeakerNotes && (
          <div
            onClick={() => setShowSpeakerNotes(false)}
            className="fixed inset-0 bg-navy-950/60 backdrop-blur-sm z-40 sm:hidden"
          />
        )}

        {/* Speaker Notes Drawer (Responsive bottom sheet on mobile, side panel on desktop) */}
        {showSpeakerNotes && (
          <aside className="speaker-notes-drawer fixed sm:absolute inset-x-0 bottom-0 sm:inset-x-auto sm:top-4 sm:right-4 sm:bottom-4 w-full sm:max-w-md max-h-[85vh] sm:max-h-none bg-white dark:bg-navy-900 border-t-2 sm:border-2 border-gold-500 rounded-t-3xl sm:rounded-2xl shadow-2xl z-50 p-5 overflow-y-auto flex flex-col justify-between animate-fade-in">
            <div>
              <div className="flex items-center justify-between border-b border-slate-200 dark:border-navy-800 pb-3 mb-4">
                <div className="flex items-center gap-2">
                  <MessageSquareQuote className="w-5 h-5 text-gold-500" />
                  <h3 className="font-bold text-sm text-slate-900 dark:text-white font-mono uppercase">
                    Speaker Notes &amp; VC Prep
                  </h3>
                </div>
                <button
                  onClick={() => setShowSpeakerNotes(false)}
                  className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-navy-800 text-slate-500"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Slide Context */}
              <div className="mb-4">
                <span className="text-[11px] font-mono text-gold-700 dark:text-gold-400 font-bold uppercase tracking-wider block">
                  {currentSlide.category} (Slide {currentSlideIndex + 1} of {totalSlides})
                </span>
                <h4 className="text-base font-bold text-slate-900 dark:text-white mt-0.5">
                  {currentSlide.notes.title}
                </h4>
              </div>

              {/* Talking Points */}
              <div className="space-y-2.5 mb-5">
                <h5 className="font-mono text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                  Key Talking Script:
                </h5>
                <ul className="space-y-2 text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                  {currentSlide.notes.talkingPoints.map((tp, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-gold-500 mt-1.5 shrink-0" />
                      <span>{tp}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Investor FAQ Prep */}
              <div className="p-3.5 rounded-xl bg-slate-100 dark:bg-navy-850 border border-slate-200 dark:border-navy-700/80 mb-4">
                <div className="font-mono text-[11px] text-gold-700 dark:text-gold-400 font-bold uppercase mb-1">
                  Expected VC Question:
                </div>
                <p className="text-xs font-semibold text-slate-900 dark:text-white mb-2">
                  &ldquo;{currentSlide.notes.investorFaq.question}&rdquo;
                </p>
                <div className="font-mono text-[11px] text-emerald-600 dark:text-emerald-400 font-bold uppercase mb-1">
                  Winning Response:
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                  {currentSlide.notes.investorFaq.answer}
                </p>
              </div>

              {/* Metrics to highlight */}
              <div>
                <h5 className="font-mono text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-2">
                  Key Numbers to Mention:
                </h5>
                <div className="flex flex-wrap gap-1.5">
                  {currentSlide.notes.metricsToHighlight.map((m, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-0.5 rounded text-[11px] font-mono bg-gold-500/10 text-gold-800 dark:text-gold-400 border border-gold-500/30 font-bold"
                    >
                      {m}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="pt-4 mt-4 border-t border-slate-200 dark:border-navy-800 text-[11px] font-mono text-slate-500 text-center">
              Swipe left/right to navigate • Press <kbd className="px-1 py-0.5 bg-slate-200 dark:bg-navy-800 rounded font-bold">N</kbd> for notes
            </div>
          </aside>
        )}
      </main>

      {/* Bottom Deck Navigation & Progress Bar (Hidden in Print) */}
      <footer className="deck-control-bar relative z-30 h-14 px-4 sm:px-6 flex items-center justify-between border-t border-slate-200 dark:border-navy-800 bg-white/95 dark:bg-navy-900/95 backdrop-blur-md">
        {/* Previous Button */}
        <button
          onClick={goToPrev}
          disabled={currentSlideIndex === 0}
          className="px-3 py-1.5 rounded-lg text-xs font-mono font-semibold flex items-center gap-1 bg-slate-100 dark:bg-navy-800 hover:bg-slate-200 dark:hover:bg-navy-700 disabled:opacity-30 disabled:pointer-events-none transition-all text-slate-800 dark:text-slate-200 border border-slate-300 dark:border-navy-700 min-h-[36px]"
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="hidden sm:inline">Previous</span>
        </button>

        {/* Center Progress Dots & Counter */}
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="hidden sm:flex items-center gap-1">
            {DECK_SLIDES.map((s, idx) => (
              <button
                key={s.id}
                onClick={() => goToSlide(idx)}
                title={`Go to slide ${idx + 1}: ${s.title}`}
                className={`h-2 rounded-full transition-all ${
                  currentSlideIndex === idx
                    ? 'w-6 bg-gold-500 shadow-gold-glow'
                    : 'w-2 bg-slate-300 dark:bg-navy-700 hover:bg-slate-400 dark:hover:bg-navy-600'
                }`}
              />
            ))}
          </div>
          <span className="font-mono text-xs font-bold text-slate-700 dark:text-slate-300">
            <span className="text-gold-600 dark:text-gold-400">
              {String(currentSlideIndex + 1).padStart(2, '0')}
            </span>
            <span className="text-slate-400 dark:text-slate-500"> / {String(totalSlides).padStart(2, '0')}</span>
          </span>
        </div>

        {/* Next Button */}
        <button
          onClick={goToNext}
          disabled={currentSlideIndex === totalSlides - 1}
          className="px-3 py-1.5 rounded-lg text-xs font-mono font-bold flex items-center gap-1 bg-gold-500 hover:bg-gold-400 text-navy-950 disabled:opacity-30 disabled:pointer-events-none transition-all shadow-sm min-h-[36px]"
        >
          <span className="hidden sm:inline">Next</span>
          <ChevronRight className="w-4 h-4" />
        </button>
      </footer>

      {/* Print-Only Multi-Page Container (Rendered during window.print()) */}
      <div className="deck-print-container hidden">
        {DECK_SLIDES.map((_, idx) => (
          <div key={idx} className="print-slide-page">
            {renderSlideContent(idx)}
          </div>
        ))}
      </div>
    </div>
  );
};
